import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/api/growth/season/api';
import PageHeader from '@components/common/PageHeader';
import { toastWarning } from '@components/common/Toast/CustomToast';
import { Alert } from '@utils/alert';
import ActionButton from '@/components/common/ActionButton/ActionButton';
import { SeasonAddReq } from '@/api/growth/season/type';
import { CorporationDefaultInfo } from '@/api/corporation/type';
import DatePicker from '@/components/common/DatePicker'; // 추가

const SeasonAdd = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const corporationInfo = location.state as CorporationDefaultInfo;

    const [formData, setFormData] = useState({
        userIdx: Number(corporationInfo.userIdx),
        name: '',
        startDt: '',
        endDt: '',
    });

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    const handleInputChange = (field: string, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // 날짜를 YYYY-MM-DD 형식으로 변환
    const formatDateString = (date: Date | null): string => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // 날짜 변경 핸들러
    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date);
        const dateString = formatDateString(date);
        handleInputChange('startDt', dateString);
    };

    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date);
        const dateString = formatDateString(date);
        handleInputChange('endDt', dateString);
    };

    // 유효성 검사
    const validateForm = async () => {
        // 대행사번호 검사
        if (!formData.userIdx) {
            toastWarning('대행사번호가 필요합니다.');
            return false;
        }

        // 필수 필드 검사
        if (!formData.name.trim()) {
            toastWarning('작기명을 입력해주세요.');
            return false;
        }
        if (!formData.startDt.trim()) {
            toastWarning('작기시작일을 입력해주세요.');
            return false;
        }
        if (!formData.endDt.trim()) {
            toastWarning('작기종료일을 입력해주세요.');
            return false;
        }

        // 날짜 유효성 검사
        if (startDate && endDate && startDate >= endDate) {
            toastWarning('작기종료일은 작기시작일보다 늦어야 합니다.');
            return false;
        }

        return true;
    };

    // 등록 핸들러
    const handleSave = async () => {
        // 유효성 검사
        const isValid = await validateForm();
        if (!isValid) return;

        await Alert.confirmAndExecute(
            '생육작기를 등록하시겠습니까?',
            '입력한 정보를 다시 한번 확인해주세요.',
            true,
            async () => {
                const addRequest: SeasonAddReq = {
                    userIdx: formData.userIdx,
                    name: formData.name,
                    startDt: formData.startDt,
                    endDt: formData.endDt,
                };

                await api.seasonAdd(addRequest).then(() => {
                    handleCancel();
                });
            },
        );
    };

    const handleCancel = (): void => {
        navigate(-1); // 이전 페이지로 돌아가기
    };

    return (
        <>
            <PageHeader
                title="생육작기 등록"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '생육작기관리' },
                    { label: '생육작기 등록', active: true },
                ]}
            />

            <div className="card custom-card mt-4">
                <div className="card-header">
                    <div className="card-title">
                        생육작기 정보를 입력해주세요.
                    </div>
                </div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                대행사번호<span className="text-danger">*</span>{' '}
                                :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.userIdx}
                                disabled
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                대행사명<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={corporationInfo.userName}
                                disabled
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                작기명<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="작기명을 입력해주세요."
                                value={formData.name}
                                onChange={e =>
                                    handleInputChange('name', e.target.value)
                                }
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                작기시작<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <DatePicker
                                selectedDate={startDate}
                                onChange={handleStartDateChange}
                                placeholder="작기 시작일을 선택해주세요"
                                maxDate={endDate || undefined}
                                showYearDropdown={false}
                                showMonthDropdown={false}
                                dropdownMode="scroll"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                작기종료<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <DatePicker
                                selectedDate={endDate}
                                onChange={handleEndDateChange}
                                placeholder="작기 종료일을 선택해주세요"
                                minDate={startDate || undefined}
                                showYearDropdown={false}
                                showMonthDropdown={false}
                                dropdownMode="scroll"
                            />
                        </div>
                    </div>
                </div>

                <div className="card-footer">
                    <div className="btn-list text-end">
                        <ActionButton type="save" onClick={handleSave} />
                        <ActionButton type="cancel" onClick={handleCancel} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default SeasonAdd;
